/* Shared horizontal rail behaviour for the Paper & Ink sections.
 *
 * Three sections now present as carousels (books, briefs, voices) and they all
 * want the same things: arrows that know when they've hit an end, keyboard
 * access, and a scrollbar that doesn't look like a scrollbar. Rather than three
 * copies of that, any element marked [data-pp-rail] gets upgraded here.
 *
 * The rail scrolls natively — no transform tracking, no index state. That keeps
 * touch, trackpad and keyboard working exactly as the platform intends, and it
 * means a rail still works perfectly if this script never loads.
 */
(function () {
  'use strict';

  function upgrade(rail) {
    if (rail.dataset.ppReady) return;
    rail.dataset.ppReady = '1';
    var wrap = rail.closest('[data-pp-railwrap]');
    if (!wrap) return;
    var prev = wrap.querySelector('[data-rail-prev]');
    var next = wrap.querySelector('[data-rail-next]');
    if (!prev || !next) return;

    function step() {
      // Advance by whole cards so nothing is ever left half-shown.
      var card = rail.firstElementChild;
      if (!card) return rail.clientWidth;
      var styles = getComputedStyle(rail);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      var w = card.getBoundingClientRect().width + gap;
      // On wide screens jumping one card at a time feels sluggish, so move a
      // viewport's worth, rounded down to a whole number of cards.
      return Math.max(w, Math.floor(rail.clientWidth / w) * w);
    }

    function sync() {
      // 2px of slack: sub-pixel layout means scrollLeft rarely lands exactly on
      // the maximum, which would otherwise leave the last arrow enabled forever.
      var max = rail.scrollWidth - rail.clientWidth;
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft >= max - 2;
      prev.disabled = atStart;
      next.disabled = atEnd;
      // A rail with nothing to scroll shouldn't advertise controls at all.
      wrap.classList.toggle('is-static', max <= 2);
    }

    var restore = null;
    function glide(left) {
      // Nothing to do, and re-issuing would restart the fight.
      if (Math.abs(rail.scrollLeft - left) < 2) return;
      rail.style.scrollSnapType = 'none';
      rail.scrollTo({ left: left, behavior: 'smooth' });
      clearTimeout(restore);
      restore = setTimeout(function () { rail.style.scrollSnapType = ''; }, 700);
    }
    function clamp(left) {
      return Math.max(0, Math.min(left, rail.scrollWidth - rail.clientWidth));
    }

    prev.addEventListener('click', function () { glide(clamp(rail.scrollLeft - step())); });
    next.addEventListener('click', function () { glide(clamp(rail.scrollLeft + step())); });
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    // Arrow keys once the rail itself has focus, without stealing them from
    // whatever the user is actually typing into.
    rail.addEventListener('keydown', function (e) {
      if (e.target !== rail) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); rail.scrollBy({ left: -step(), behavior: 'smooth' }); }
      if (e.key === 'ArrowRight') { e.preventDefault(); rail.scrollBy({ left: step(), behavior: 'smooth' }); }
    });

    sync();
    // Images land after first paint and change scrollWidth, so re-check once
    // they have. Without this a rail can start with both arrows disabled.
    window.addEventListener('load', sync);
    if (window.ResizeObserver) new ResizeObserver(sync).observe(rail);

    /* Autoplay, for rails that are a slideshow rather than a browsable list.
     * It advances by whole cards and wraps at the end, and it steps aside the
     * moment the reader shows any interest — hover, focus, touch, or their own
     * scroll — because a slideshow that keeps moving under someone reading it
     * is worse than one that never moved. */
    var every = parseInt(rail.dataset.ppAuto || '0', 10);
    if (!every) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var timer = null, held = false;
    function tick() {
      if (held || document.hidden) return;
      var max = rail.scrollWidth - rail.clientWidth;
      if (max <= 2) return;
      glide(rail.scrollLeft >= max - 2 ? 0 : clamp(rail.scrollLeft + step()));
    }
    function start() { clearInterval(timer); timer = setInterval(tick, every); }
    function hold(on) { held = on; }

    ['pointerenter', 'focusin', 'touchstart'].forEach(function (e) {
      rail.addEventListener(e, function () { hold(true); }, { passive: true });
    });
    ['pointerleave', 'focusout'].forEach(function (e) {
      rail.addEventListener(e, function () { hold(false); });
    });
    // A manual arrow press should not be immediately overridden by the timer.
    wrap.addEventListener('click', start);
    document.addEventListener('visibilitychange', start);

    // Only run while the rail is actually on screen.
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) start(); else clearInterval(timer);
      }, { threshold: 0.25 }).observe(rail);
    } else {
      start();
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-pp-rail]').forEach(upgrade);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // The theme editor re-renders a section in place; its markup is brand new, so
  // the listeners above are gone with the old nodes.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();


/* Run animations only while their block is on screen.
 *
 * Eighteen infinite CSS animations were ticking from page load — step
 * previews, the photo marquee, the trust ticker — and each frame of each of
 * them costs style, layout and paint on the main thread. Lighthouse put 3.5s
 * into Style & Layout and 1.0s into Rendering on a mid-range phone, which is
 * what was holding first paint at nearly five seconds.
 *
 * Pausing rather than stopping: a paused animation costs nothing per frame and
 * resumes exactly where it left off, so nothing is ever seen mid-jump.
 */
(function () {
  'use strict';
  var scopes = document.querySelectorAll('.pp-stage, .pp-wall, .pp-strip');
  if (!scopes.length) return;
  if (!('IntersectionObserver' in window)) {
    scopes.forEach(function (el) { el.classList.add('is-live'); });
    return;
  }
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { e.target.classList.toggle('is-live', e.isIntersecting); });
  }, { rootMargin: '10% 0px' });
  scopes.forEach(function (el) { io.observe(el); });
})();

/* ── In-page anchors that land where they should ────────────────────────────
   Clicking a nav link scrolled to the right place and then drifted, because
   things below the fold finish loading afterwards and change the height above
   the target: the Judge.me widget sits immediately before the FAQ, so #faq
   scrolled correctly and was then pushed down past the reviews. The example
   covers do the same thing — they are fetched by an observer with a 150%
   margin, so a jump into that section lands before the images have size.

   So: scroll, then check again as things settle, and correct if the target has
   moved. Any deliberate scroll by the person reading cancels it immediately —
   a page that keeps yanking itself back is worse than one that lands short. */
(function () {
  var CHECKS = [120, 400, 900, 1600];
  var timers = [];
  var watching = false;

  function stop() {
    watching = false;
    timers.forEach(clearTimeout);
    timers = [];
  }

  ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
    window.addEventListener(evt, function () { if (watching) stop(); }, { passive: true });
  });

  function settle(target) {
    if (!target) return;
    stop();
    watching = true;
    CHECKS.forEach(function (ms) {
      timers.push(setTimeout(function () {
        if (!watching || !document.contains(target)) return;
        var top = target.getBoundingClientRect().top;
        // Anything inside a few pixels is already right; only correct real drift.
        if (Math.abs(top - offsetFor(target)) < 6) return;
        target.scrollIntoView({ block: 'start' });
      }, ms));
    });
    timers.push(setTimeout(stop, CHECKS[CHECKS.length - 1] + 300));
  }

  // After a correct scroll the target sits exactly scroll-margin-top from the
  // viewport edge, so that is what "already right" means. Read it back rather
  // than keeping a second copy of the number in here.
  function offsetFor(el) {
    var m = parseFloat(getComputedStyle(el).scrollMarginTop);
    return isNaN(m) ? 0 : m;
  }

  function fromHash() {
    var h = window.location.hash;
    if (!h || h.length < 2) return null;
    try { return document.getElementById(decodeURIComponent(h.slice(1))); }
    catch (e) { return null; }
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href*="#"]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    var hash = href.slice(href.indexOf('#') + 1);
    if (!hash) return;
    // Same-document links only — a hash on another page is a normal navigation.
    var url;
    try { url = new URL(href, window.location.href); } catch (err) { return; }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
    var target = document.getElementById(hash);
    if (target) settle(target);
  }, true);

  window.addEventListener('hashchange', function () { settle(fromHash()); });
  window.addEventListener('load', function () { if (window.location.hash) settle(fromHash()); });
})();
